/**
 * The 15-status workflow engine, pure and total.
 *
 * Beam numbers these statuses in every document it writes, so the numbers
 * are the identity here and the labels are Beam's. The rules live in this
 * file and nowhere else: not in a route, not in a screen. A caller passes
 * the event and what it knows about the matter, and gets back either the
 * new status with any flags the transition raised, or a refusal with the
 * reason, in words a screen can show.
 *
 * Facts encoded from Beam's flow document (docs/PLAN.md Part 5):
 * paperwork is sorted (3 to 4) only when the QA sort review is complete;
 * a file review needs no appointment and skips 5; plaintiff referrers are
 * invoiced before release (9, 11, 10, 12) and everyone else after (9, 10,
 * 11, 12); a reason is mandatory for Awaiting Information, DNA and
 * Cancelled; a cancellation inside the late window is flagged; a repeat DNA
 * is flagged. Beam answered docs/OPEN.md item 6 on 5 September 2026: a
 * matter may be approved before its LOI is reviewed, so the LOI gate on
 * approval is off by default and kept as a config flag (D-0013).
 */
export const STATUSES = {
    1: { key: 'awaiting_approval', label: 'Awaiting Approval' },
    2: { key: 'paperwork_outstanding', label: 'Paperwork Outstanding' },
    3: { key: 'paperwork_received', label: 'Paperwork Received' },
    4: { key: 'paperwork_sorted', label: 'Paperwork Sorted' },
    5: { key: 'awaiting_appointment', label: 'Awaiting Appointment' },
    6: { key: 'awaiting_doctor_draft', label: 'Awaiting Doctor Draft' },
    7: { key: 'awaiting_qa', label: 'Awaiting QA' },
    8: { key: 'awaiting_doctor_review', label: 'Awaiting Doctor Review' },
    9: { key: 'qa_complete', label: 'QA Complete' },
    10: { key: 'finalised_released', label: 'Finalised and Released' },
    11: { key: 'awaiting_invoicing', label: 'Awaiting Invoicing' },
    12: { key: 'invoiced_closed', label: 'Invoiced / Closed' },
    13: { key: 'awaiting_information', label: 'Awaiting Information' },
    14: { key: 'dna', label: 'DNA' },
    15: { key: 'cancelled', label: 'Cancelled' },
};
export const ALL_STATUSES = Object.keys(STATUSES).map(Number);
export const TERMINAL_STATUSES = [15];
/** Statuses a cancellation may come from: anything before release, plus the parking states. */
export const ACTIVE_STATUSES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 13, 14];
export function statusLabel(status) {
    return STATUSES[status].label;
}
/**
 * Display forms for the report types, kept beside the statuses so every
 * screen and export agrees. The short forms are the abbreviations Beam's
 * documents use; confirm with Beam (docs/OPEN.md item 45).
 */
export const REPORT_TYPES = {
    mva: { short: 'MVA', label: 'Motor vehicle accident' },
    ime: { short: 'IME', label: 'Independent medical examination' },
    file_review: { short: 'File review', label: 'File review' },
    fitness_for_work: { short: 'FFW', label: 'Fitness for work' },
    supplementary: { short: 'Supp.', label: 'Supplementary report' },
    permanent_impairment: { short: 'PI', label: 'Permanent impairment assessment' },
    consultation_advice: { short: 'Consult', label: 'Consultation advice' },
};
export const ALL_REPORT_TYPES = Object.keys(REPORT_TYPES);
export function reportTypeLabel(kind, form = 'short') {
    return REPORT_TYPES[kind][form];
}
/**
 * Display forms for the events, in Beam's voice: the verb on the button and
 * in the log, and the label while the move is in flight. Held beside the
 * events so screens, toasts and audit summaries use the same words
 * (docs/OPEN.md item 48 asks Beam to confirm the doctor and QA verbs).
 */
export const EVENT_DISPLAY = {
    approve: { label: 'Approve', busy: 'Moving' },
    paperwork_received: { label: 'Mark paperwork received', busy: 'Moving' },
    sorted: { label: 'Mark sorted', busy: 'Moving' },
    appointment_booked: { label: 'Book appointment', busy: 'Moving' },
    no_appointment_required: { label: 'No appointment required', busy: 'Moving' },
    attended: { label: 'Record attendance', busy: 'Moving' },
    doctor_submits: { label: 'Submit draft', busy: 'Moving' },
    qa_submits: { label: 'Send to doctor', busy: 'Moving' },
    doctor_resubmits: { label: 'Resubmit to QA', busy: 'Moving' },
    qa_completes: { label: 'Complete QA', busy: 'Moving' },
    release: { label: 'Release', busy: 'Moving' },
    invoice: { label: 'Raise invoice', busy: 'Moving' },
    close: { label: 'Close', busy: 'Moving' },
    park_awaiting_information: { label: 'Park: awaiting information', busy: 'Parking' },
    resume: { label: 'Resume', busy: 'Resuming' },
    dna: { label: 'Record DNA', busy: 'Recording DNA' },
    rebook: { label: 'Rebook', busy: 'Moving' },
    cancel: { label: 'Cancel case', busy: 'Cancelling' },
    reopen: { label: 'Reopen', busy: 'Reopening' },
};
export function eventLabel(kind) {
    return EVENT_DISPLAY[kind].label;
}
export const DEFAULT_ENGINE_CONFIG = {
    lateCancelHours: 72,
    parkableFrom: [4, 5, 6, 7, 8],
    requireLoiApprovalBeforeApproval: false,
};
const NEEDS_NO_APPOINTMENT = ['file_review', 'supplementary'];
function refuse(from, event, reason) {
    return { moved: false, from, event: event.kind, reason };
}
function move(from, to, event, flags = {}) {
    return { moved: true, from, to, event: event.kind, flags };
}
function needsReason(event) {
    return 'reason' in event;
}
export function transition(from, event, facts, config = DEFAULT_ENGINE_CONFIG) {
    if (needsReason(event) && event.reason.trim() === '') {
        return refuse(from, event, `${statusLabel(from)}: a reason is required for ${event.kind.replace(/_/g, ' ')}`);
    }
    if (TERMINAL_STATUSES.includes(from)) {
        return refuse(from, event, `${statusLabel(from)} is terminal`);
    }
    switch (event.kind) {
        case 'cancel': {
            if (!ACTIVE_STATUSES.includes(from))
                return refuse(from, event, `cannot cancel from ${statusLabel(from)}: the report has been released`);
            const late = facts.hoursToAppointment !== undefined && facts.hoursToAppointment >= 0 && facts.hoursToAppointment < config.lateCancelHours;
            return move(from, 15, event, late ? { lateCancellation: true } : {});
        }
        case 'park_awaiting_information': {
            if (from === 13)
                return refuse(from, event, 'the case is already awaiting information');
            if (!config.parkableFrom.includes(from))
                return refuse(from, event, `cannot park from ${statusLabel(from)}`);
            if (event.items < 1)
                return refuse(from, event, 'at least one outstanding item must be listed');
            return move(from, 13, event, { parkedFrom: from });
        }
        case 'resume': {
            if (from !== 13)
                return refuse(from, event, 'only a case awaiting information can resume');
            if (!facts.parkedFrom)
                return refuse(from, event, 'the status the case was parked from is not recorded');
            return move(from, facts.parkedFrom, event);
        }
        default:
            break;
    }
    switch (from) {
        case 1:
            if (event.kind === 'approve') {
                if (config.requireLoiApprovalBeforeApproval && facts.hasLetter && !facts.loiApproved) {
                    return refuse(from, event, 'the letter of instruction has not been reviewed and approved');
                }
                return move(from, 2, event);
            }
            break;
        case 2:
            if (event.kind === 'paperwork_received')
                return move(from, 3, event);
            break;
        case 3:
            if (event.kind === 'sorted') {
                if (!facts.sortReviewComplete)
                    return refuse(from, event, 'the casefile sort review is not complete');
                return move(from, 4, event);
            }
            break;
        case 4:
            if (event.kind === 'appointment_booked') {
                if (NEEDS_NO_APPOINTMENT.includes(facts.reportType) && facts.reportType === 'file_review') {
                    return refuse(from, event, 'a file review needs no appointment: it skips Awaiting Appointment and goes straight to the doctor');
                }
                return move(from, 5, event);
            }
            if (event.kind === 'no_appointment_required') {
                if (!NEEDS_NO_APPOINTMENT.includes(facts.reportType))
                    return refuse(from, event, `${facts.reportType.replace(/_/g, ' ')} needs an appointment`);
                return move(from, 6, event, { skippedAppointment: true });
            }
            break;
        case 5:
            if (event.kind === 'attended')
                return move(from, 6, event);
            if (event.kind === 'dna')
                return move(from, 14, event, { repeatDna: facts.priorDna, dnaFeeSuggested: true });
            break;
        case 14:
            if (event.kind === 'rebook')
                return move(from, 5, event);
            break;
        case 6:
            if (event.kind === 'doctor_submits')
                return move(from, 7, event);
            break;
        case 7:
            if (event.kind === 'qa_submits') {
                if (facts.openSuggestions < 1)
                    return refuse(from, event, 'QA has nothing for the doctor to decide; complete QA instead');
                return move(from, 8, event);
            }
            if (event.kind === 'qa_completes') {
                if (facts.openSuggestions > 0)
                    return refuse(from, event, 'suggestions are still open with the doctor');
                return move(from, 9, event);
            }
            break;
        case 8:
            if (event.kind === 'doctor_resubmits') {
                if (facts.openSuggestions > 0)
                    return refuse(from, event, 'the doctor has not decided every suggestion');
                return move(from, 7, event);
            }
            break;
        case 9:
            if (event.kind === 'release') {
                if (facts.referrerKind === 'plaintiff')
                    return refuse(from, event, 'a plaintiff referrer is invoiced before release; raise the invoice first');
                if (!facts.finalisationChecklistComplete)
                    return refuse(from, event, 'the finalisation checklist is not complete');
                return move(from, 10, event);
            }
            if (event.kind === 'invoice') {
                if (facts.referrerKind !== 'plaintiff')
                    return refuse(from, event, 'a standard referrer receives the report with the invoice; release first');
                if (!facts.finalisationChecklistComplete)
                    return refuse(from, event, 'the finalisation checklist is not complete');
                return move(from, 11, event);
            }
            break;
        case 10:
            if (event.kind === 'invoice') {
                if (facts.referrerKind === 'plaintiff')
                    return refuse(from, event, 'a plaintiff case was invoiced before release');
                return move(from, 11, event);
            }
            if (event.kind === 'close') {
                if (facts.referrerKind !== 'plaintiff')
                    return refuse(from, event, 'invoice before closing');
                return move(from, 12, event);
            }
            break;
        case 11:
            if (event.kind === 'release') {
                if (facts.referrerKind !== 'plaintiff')
                    return refuse(from, event, 'the report has already been released');
                if (!facts.paymentReceived)
                    return refuse(from, event, 'payment has not been received; the report is held until it is');
                return move(from, 10, event);
            }
            if (event.kind === 'close') {
                if (facts.referrerKind === 'plaintiff')
                    return refuse(from, event, 'release the paid report before closing');
                return move(from, 12, event);
            }
            break;
        case 12:
            if (event.kind === 'reopen')
                return move(from, 9, event);
            break;
        case 13:
            break;
        default:
            break;
    }
    return refuse(from, event, `${event.kind.replace(/_/g, ' ')} is not allowed from ${statusLabel(from)}`);
}
/** Every event that would move the matter from `from` under the given facts; screens use this to render only real actions. */
export function availableEvents(from, facts, config = DEFAULT_ENGINE_CONFIG) {
    const probes = [
        { kind: 'approve' }, { kind: 'paperwork_received' }, { kind: 'sorted' }, { kind: 'appointment_booked' },
        { kind: 'no_appointment_required' }, { kind: 'attended' }, { kind: 'doctor_submits' }, { kind: 'qa_submits' },
        { kind: 'doctor_resubmits' }, { kind: 'qa_completes' }, { kind: 'release' }, { kind: 'invoice' }, { kind: 'close' },
        { kind: 'park_awaiting_information', items: 1, reason: 'probe' }, { kind: 'resume' }, { kind: 'dna', reason: 'probe' },
        { kind: 'rebook' }, { kind: 'cancel', reason: 'probe' }, { kind: 'reopen', reason: 'probe' },
    ];
    return probes.filter((e) => transition(from, e, facts, config).moved).map((e) => e.kind);
}
//# sourceMappingURL=status.js.map